import { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { ContactRole, IContact } from 'clickNgo/entities/contact'
import {
  ContactRolesRemoving,
  FlagToReassign,
  RoleReassignmentOption,
  RoleToReassign,
} from 'clickNgo/features/contact/contactReassignment'
import { Nullable } from 'cng-common/models/utils'

import { rolesToKeepForBranchOnline } from '../configs'

export interface ReassignmentContextType {
  availableContacts: IContact[]
  rolesToReassign: RoleToReassign[]
  uniqueRolesToReassign: RoleToReassign[]
  flagsToReassign: FlagToReassign[]
  updateRoleAssignment: (role: string, contact: Nullable<IContact>) => void
  updateUniqueRoleAssignment: (role: string, contact: Nullable<IContact>) => void
  updateFlagAssignment: (flagCode: string, contact: Nullable<IContact>) => void
  setAvailableContacts: (contacts: IContact[]) => void
  setRolesToReassign: (roles: RoleToReassign[]) => void
  rolesForRemovingFromRelinkingContacts: string[]
  targetsContactsWithRoleForRemoving: ContactRolesRemoving[]
  setTargetPartnerContactsRoles: (roles: ContactRolesRemoving[]) => void
  setUniqueRolesToReassign: (roles: RoleToReassign[]) => void
  setFlagsToReassign: (flags: FlagToReassign[]) => void
  reassignmentUniqueRolesOption: RoleReassignmentOption | null
  setReassignmentUniqueRolesOption: (option: RoleReassignmentOption | null) => void
  isAskingHowRemoveUniqueRoles: boolean
  setIsAskingHowRemoveUniqueRoles: (flag: boolean) => void
  commonUniqueRolesWithTarget: string[]
}

export const ReassignmentContext = createContext<ReassignmentContextType | null>(null)

export const useReassignment = (): ReassignmentContextType => {
  const context = useContext(ReassignmentContext)
  if (!context) {
    throw new Error('useReassignment must be used within ReassignmentProvider')
  }
  return context
}

export interface ReassignmentProviderProps {
  children: React.ReactNode
}

export const ReassignmentProvider: React.FC<ReassignmentProviderProps> = ({ children }) => {
  const [availableContacts, setAvailableContacts] = useState<IContact[]>([])
  const [rolesToReassign, setRolesToReassign] = useState<RoleToReassign[]>([])
  const [flagsToReassign, setFlagsToReassign] = useState<FlagToReassign[]>([])
  const [uniqueRolesToReassign, setUniqueRolesToReassign] = useState<RoleToReassign[]>([])
  const [targetPartnerContactsRoles, setTargetPartnerContactsRoles] = useState<
    ContactRolesRemoving[]
  >([])

  const [isAskingHowRemoveUniqueRoles, setIsAskingHowRemoveUniqueRoles] = useState(false)

  const [reassignmentUniqueRolesOption, setReassignmentUniqueRolesOption] =
    useState<RoleReassignmentOption | null>(null)

  const updateRoleAssignment = useCallback((role: string, contact: Nullable<IContact>) => {
    setRolesToReassign((prevRoles) =>
      prevRoles.map((r) => (r.role === role ? { ...r, contact } : r))
    )
  }, [])

  const updateUniqueRoleAssignment = useCallback((role: string, contact: Nullable<IContact>) => {
    setUniqueRolesToReassign((prevRoles) =>
      prevRoles.map((r) => (r.role === role ? { ...r, contact } : r))
    )
  }, [])

  const updateFlagAssignment = useCallback((flagCode: string, contact: Nullable<IContact>) => {
    setFlagsToReassign((prevFlags) =>
      prevFlags.map((f) => (f.flag.code === flagCode ? { ...f, contact } : f))
    )
  }, [])

  const commonUniqueRolesWithTarget = useMemo(() => {
    const flatRelinkingUniqueRoles = uniqueRolesToReassign.map((el) => el.role) as string[]

    const targetRoles = targetPartnerContactsRoles.flatMap((el) => el.rolesForRemoving)

    return flatRelinkingUniqueRoles.filter((role) => targetRoles.includes(role))
  }, [uniqueRolesToReassign, targetPartnerContactsRoles])

  const rolesForRemovingFromRelinkingContacts = useMemo(() => {
    const flatRelinkingUniqueRoles = uniqueRolesToReassign.map((el) => el.role) as ContactRole[]

    if (isAskingHowRemoveUniqueRoles) {
      if (
        reassignmentUniqueRolesOption === RoleReassignmentOption.RemoveFromTargetPartnerContacts
      ) {
        return []
      }
      return commonUniqueRolesWithTarget
    }

    return flatRelinkingUniqueRoles.filter((el) => !rolesToKeepForBranchOnline.includes(el))
  }, [
    uniqueRolesToReassign,
    isAskingHowRemoveUniqueRoles,
    reassignmentUniqueRolesOption,
    commonUniqueRolesWithTarget,
  ])

  const targetsContactsWithRoleForRemoving = useMemo(() => {
    if (isAskingHowRemoveUniqueRoles) {
      if (reassignmentUniqueRolesOption === RoleReassignmentOption.RemoveFromMovingContact) {
        return []
      }
      return targetPartnerContactsRoles
    }

    return targetPartnerContactsRoles
      .map((el) => ({
        ...el,
        rolesForRemoving: el.rolesForRemoving.filter((role) =>
          rolesToKeepForBranchOnline.includes(role as ContactRole)
        ),
      }))
      .filter((el) => el.rolesForRemoving.length > 0)
  }, [isAskingHowRemoveUniqueRoles, reassignmentUniqueRolesOption, targetPartnerContactsRoles])

  const value = useMemo(
    (): ReassignmentContextType => ({
      availableContacts,
      rolesToReassign,
      uniqueRolesToReassign,
      flagsToReassign,
      updateRoleAssignment,
      updateUniqueRoleAssignment,
      updateFlagAssignment,
      setAvailableContacts,
      setRolesToReassign,
      setFlagsToReassign,
      setUniqueRolesToReassign,
      setTargetPartnerContactsRoles,
      reassignmentUniqueRolesOption,
      setReassignmentUniqueRolesOption,
      targetsContactsWithRoleForRemoving,
      isAskingHowRemoveUniqueRoles,
      setIsAskingHowRemoveUniqueRoles,
      rolesForRemovingFromRelinkingContacts,
      commonUniqueRolesWithTarget,
    }),
    [
      availableContacts,
      rolesToReassign,
      uniqueRolesToReassign,
      flagsToReassign,
      updateRoleAssignment,
      updateUniqueRoleAssignment,
      updateFlagAssignment,
      reassignmentUniqueRolesOption,
      targetsContactsWithRoleForRemoving,
      isAskingHowRemoveUniqueRoles,
      rolesForRemovingFromRelinkingContacts,
      commonUniqueRolesWithTarget,
    ]
  )

  return <ReassignmentContext.Provider value={value}>{children}</ReassignmentContext.Provider>
}
